import { useEffect } from "react";
import {
  ArrowRight,
  BookOpen,
  Building2,
  CheckCircle2,
  Code2,
  Smartphone,
  type LucideIcon,
} from "lucide-react";
import { Link, useLocation } from "wouter";

import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PRODUCTS } from "@base/catalog/products";
import { getHubCopy, type HubCopy } from "@base/content";

type DoorId = HubCopy["doors"][number]["id"];

const DOOR_ICONS: Record<DoorId, LucideIcon> = {
  apps: Smartphone,
  business: Building2,
  developers: Code2,
};

/**
 * Anchors of the old single-page home, still linked from outside (ads,
 * shared links, old app builds). Each one now lives on a section page.
 */
const LEGACY_HASHES: Record<string, string> = {
  "#apps": "/apps",
  "#tecnologias": "/business#pwa",
  "#custom": "/business#servicos",
  "#contact": "/business#contato",
};

function useLegacyHashRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => {
    const target = LEGACY_HASHES[window.location.hash];
    if (target) navigate(target, { replace: true });
  }, [navigate]);
}

export default function HubPage() {
  useLegacyHashRedirect();
  const copy = getHubCopy();
  const ownApps = PRODUCTS.filter(product =>
    (product.audiences as readonly string[]).includes("apps")
  ).length;
  const stats = [
    { value: String(ownApps), label: copy.about.appsLabel },
    ...copy.about.stats,
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main id="main-content" className="flex-1">
        {/* Hero */}
        <section className="pt-28 md:pt-36 pb-12 md:pb-16 bg-gradient-to-br from-background via-background to-primary/5">
          <div className="container max-w-4xl text-center">
            <p className="inline-block px-4 py-2 mb-6 bg-primary text-primary-foreground rounded-full text-sm font-semibold">
              {copy.hero.eyebrow}
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              {copy.hero.heading.before}{" "}
              <span className="bg-gradient-to-r from-primary via-[oklch(0.50_0.214_240)] to-[oklch(0.50_0.214_200)] bg-clip-text text-transparent">
                {copy.hero.heading.highlight}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              {copy.hero.lead}
            </p>
          </div>
        </section>

        {/* One door per audience */}
        <section aria-labelledby="portas-titulo" className="py-12 md:py-16">
          <div className="container">
            <h2
              id="portas-titulo"
              className="text-2xl md:text-3xl font-bold text-center mb-10"
            >
              {copy.doorsHeading}
            </h2>
            <ul className="grid gap-6 lg:grid-cols-3">
              {copy.doors.map(door => {
                const Icon = DOOR_ICONS[door.id];
                return (
                  <li key={door.id}>
                    <Card className="h-full border-border hover:border-primary/50 hover:shadow-lg transition-all">
                      <CardContent className="p-6 md:p-8 flex flex-col gap-4 h-full">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                          <Icon
                            className="text-primary"
                            size={24}
                            aria-hidden="true"
                          />
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-1">
                            {door.subtitle}
                          </p>
                          <h3 className="text-2xl font-semibold">
                            {door.title}
                          </h3>
                        </div>
                        <p className="text-muted-foreground">
                          {door.description}
                        </p>
                        <ul className="space-y-2">
                          {door.highlights.map(item => (
                            <li
                              key={item}
                              className="flex items-start gap-2 text-sm text-muted-foreground"
                            >
                              <CheckCircle2
                                size={16}
                                className="text-primary flex-shrink-0 mt-0.5"
                                aria-hidden="true"
                              />
                              {item}
                            </li>
                          ))}
                        </ul>
                        <div className="mt-auto pt-2">
                          <Button asChild>
                            <Link href={door.cta.href}>
                              {door.cta.label}
                              <ArrowRight
                                className="ml-2"
                                size={18}
                                aria-hidden="true"
                              />
                            </Link>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* About */}
        <section
          aria-labelledby="sobre-titulo"
          className="py-12 md:py-16 bg-card"
        >
          <div className="container grid gap-10 lg:grid-cols-2 items-center">
            <div>
              <h2
                id="sobre-titulo"
                className="text-2xl md:text-3xl font-bold mb-4"
              >
                {copy.about.heading}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {copy.about.text}
              </p>
            </div>
            <dl className="flex flex-wrap gap-x-12 gap-y-6 lg:justify-center">
              {stats.map(stat => (
                <div key={stat.label}>
                  <dt className="text-sm text-muted-foreground">
                    {stat.label}
                  </dt>
                  <dd className="text-4xl font-bold text-primary">
                    {stat.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* Blog and contact */}
        <section className="py-12 md:py-16">
          <div className="container grid gap-6 md:grid-cols-2">
            <Card>
              <CardContent className="p-6 md:p-8 flex flex-col gap-3 h-full">
                <BookOpen
                  className="text-primary"
                  size={24}
                  aria-hidden="true"
                />
                <h2 className="text-xl font-semibold">{copy.blog.heading}</h2>
                <p className="text-muted-foreground">{copy.blog.text}</p>
                <div className="mt-auto pt-2">
                  <Button asChild variant="outline">
                    <Link href={copy.blog.cta.href}>{copy.blog.cta.label}</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-primary text-primary-foreground border-primary">
              <CardContent className="p-6 md:p-8 flex flex-col gap-3 h-full">
                <h2 className="text-xl font-semibold">
                  {copy.contact.heading}
                </h2>
                <p className="text-primary-foreground/90">
                  {copy.contact.text}
                </p>
                <div className="mt-auto pt-2">
                  <Button asChild variant="secondary">
                    <Link href={copy.contact.cta.href}>
                      {copy.contact.cta.label}
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
